import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAccessAssignments, getAdminDirectoryUsers, getCurrentAccess, getEmployeeDirectoryUsers, updateAccessAssignment } from '@/api/access';
import type { UserAccess } from '@/types/access';

/**
 * Fetches the access/authorization profile of the currently signed-in user.
 * @returns TanStack Query result containing the current user's access.
 */
export function useCurrentAccess() {
  return useQuery({ queryKey: ['access', 'me'], queryFn: getCurrentAccess, staleTime: 60_000 });
}

/**
 * Fetches all user-to-role access assignments (admin view).
 * @param enabled - Whether the query should run automatically.
 * @returns TanStack Query result containing the access assignments.
 */
export function useAccessAssignments(enabled = true) {
  return useQuery({ queryKey: ['access', 'assignments'], queryFn: getAccessAssignments, enabled });
}

/**
 * Fetches the directory users belonging to the admin group (e.g. from Azure AD).
 * Does not retry on failure since directory lookups depend on an external identity provider.
 * @param enabled - Whether the query should run automatically.
 * @returns TanStack Query result containing the admin group's directory users.
 */
export function useAdminDirectoryUsers(enabled = true) {
  return useQuery({ queryKey: ['access', 'directory', 'admin-group'], queryFn: getAdminDirectoryUsers, enabled, staleTime: 300_000, retry: false });
}

/**
 * Fetches the directory users belonging to the employee group (e.g. from Azure AD).
 * Does not retry on failure since directory lookups depend on an external identity provider.
 * @param enabled - Whether the query should run automatically.
 * @returns TanStack Query result containing the employee group's directory users.
 */
export function useEmployeeDirectoryUsers(enabled = true) {
  return useQuery({ queryKey: ['access', 'directory', 'employee-group'], queryFn: getEmployeeDirectoryUsers, enabled, staleTime: 300_000, retry: false });
}

/**
 * Creates or updates a user's access assignment, then invalidates access and
 * related administration audit data so both refresh with the new state.
 * @returns Mutation accepting the {@link UserAccess} value to persist.
 */
export function useUpdateAccess() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (value: UserAccess) => updateAccessAssignment(value),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['access'] });
      // Access changes are audited under the Administration module; refresh that view too.
      void client.invalidateQueries({ queryKey: ['audit', { module: 'Administration' }] });
    },
  });
}
